import { usageTracker, UsageTrackerEvent } from "@volumental/analytic";
import arrowIcon from "@volumental/assets/icons/backArrow.black.svg";
import introImg from "@volumental/assets/images/size-chart/scan-thumbnail.gif";
import { LoaderDots } from "@volumental/common";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useSessionStore, useViewStore } from "../../../store";
import { AppMode } from "../../../types/types";
import type { RecommendationError, SizeRecommendation } from "../../../types/types";
import { formatSizeLocale, getStartView } from "./scanner-controller-helper";

interface ButtonContentProps {
  recommendedSize?: SizeRecommendation;
  showWidth?: boolean;
  error?: RecommendationError;
  loading: boolean;
}

function ButtonContent({ recommendedSize, showWidth, error, loading }: ButtonContentProps): JSX.Element {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="px-4">
        <LoaderDots />
      </div>
    );
  }
  if (error?.code === "SIZE_NOT_AVAILABLE") {
    return (
      <div className="text-black text-left text-sm font-normal flex flex-col mx-4">
        {t("scannerButton.notAvailableInYourSize")}
      </div>
    );
  }
  if (recommendedSize) {
    return (
      <div className="text-black text-left text-sm font-normal flex flex-col mx-4">
        <span>{t("scannerButton.yourSize")}</span>
        <span className="text-xl font-bold">
          {formatSizeLocale(recommendedSize.sizeLocale)} {recommendedSize.lengthSize}{" "}
          {showWidth && recommendedSize.widthSize ? `(${recommendedSize.widthSize.replace("1", "")})` : null}
        </span>
      </div>
    );
  }
  return (
    <div className="text-black text-left text-sm font-normal flex flex-col mx-4">
      <span>{t("scannerButton.volumentalSizing")}</span>
      <span className="text-xl font-bold">{t("scannerButton.findYourSize")}</span>
    </div>
  );
}

export function ScannerBorderedButton({
  recommendedSize,
  showWidth = true,
  error,
  loading,
}: ButtonContentProps): JSX.Element {
  const { setMode } = useSessionStore((store) => store);
  const { setCurrentView } = useViewStore((store) => store);
  const { t } = useTranslation();
  const goToScannerFlow = (): void => {
    usageTracker.track(UsageTrackerEvent.GoToScannerFromSizeChart);
    setMode(AppMode.Recommendation);
    setCurrentView(getStartView(recommendedSize !== undefined || error?.code === "SIZE_NOT_AVAILABLE"));
  };

  return (
    <button
      className="flex flex-row items-center justify-start w-full bg-white rounded-2xl border-black border p-3"
      onClick={goToScannerFlow}
    >
      <div className="flex flex-row items-center justify-between w-full">
        <div className="flex flex-row justify-center items-center">
          <img
            src={introImg}
            role="presentation"
            alt={t("imageAlt.scannerBt")}
            className="max-w-14 max-h-14 shrink-0 rounded-lg"
          />
          <ButtonContent recommendedSize={recommendedSize} showWidth={showWidth} error={error} loading={loading} />
        </div>
        <img className="relative h-7 w-7 rotate-180 m-0 p-0" src={arrowIcon} alt={t("imageAlt.arrowIcon")} />
      </div>
    </button>
  );
}
